import { clearCachedItems, LAST_ACCOUNT_ID_ATTR } from '@/utils/storage'
import { jwtDecode } from 'jwt-decode'
import { resetRouter, addRoutes } from '@/router'
import { usePermissionStore } from '@/stores/permission'
import { useAuthStore } from '@/stores/auth'
import { reloadSessionData, resetSessionStores } from '@/stores/session'

export const CURRENT_ACCOUNT_SIGNATURE_ATTR = 'cas'

export function getLastAccountId() {
  try {
    return localStorage.getItem(LAST_ACCOUNT_ID_ATTR)
  } catch {
    return null
  }
}

/** "<account_id>|<sub>" of a signature; null when it cannot be decoded. */
function identityOf(signature) {
  try {
    const data = jwtDecode(signature)
    return `${data.account_id ?? ''}|${data.sub ?? ''}`
  } catch {
    return null
  }
}

/**
 * Who the stores' session data belongs to. Seeded from the stored signature, so a switch after a
 * page reload is still recognised; the refresh path removes the expired `cas` before it calls
 * setCurrentAccount, so the previous signature cannot be read there.
 */
let sessionIdentity = (() => {
  try {
    const stored = localStorage.getItem(CURRENT_ACCOUNT_SIGNATURE_ATTR)
    return stored ? identityOf(stored) : null
  } catch {
    return null
  }
})()

/** Logout: the next signature starts a new session without a reset of its own (logout did it). */
export function forgetSessionIdentity() {
  sessionIdentity = null
}

export function setCurrentAccount(account_signature) {
  localStorage.removeItem(CURRENT_ACCOUNT_SIGNATURE_ATTR)
  clearCachedItems()
  localStorage.setItem(CURRENT_ACCOUNT_SIGNATURE_ATTR, account_signature)
  const account_data = jwtDecode(account_signature)
  if (account_data.account_id) {
    localStorage.setItem(LAST_ACCOUNT_ID_ATTR, account_data.account_id)
  }
  // The acting users row of the person in this account.
  useAuthStore().setUserId(account_data.sub ?? null)
  resetRouter()
  // A real switch (other account or other user) starts a new session: the other account has other
  // mailboxes, spaces, users (or the user is a portal contact there). The hourly refresh of the
  // same account's signature also comes through here and must keep everything.
  const identity = identityOf(account_signature)
  const switched = sessionIdentity !== null && identity !== sessionIdentity
  if (switched) resetSessionStores()
  sessionIdentity = identity
  const permissionStore = usePermissionStore()
  permissionStore.setRoles(account_data.roles)
  const accessRoutes = permissionStore.generateRoutes({
    roles: account_data.roles,
    cff: account_data.cff || []
  })
  addRoutes(accessRoutes)
  // The new `cas` is stored: the reloads already go to the new account.
  if (switched) reloadSessionData()
  return account_signature
}
